import { useState } from 'react';
import { Download } from 'iconoir-react';
import { useTranslation } from 'react-i18next';
import { Badge } from 'components/ui/badge';
import { Button } from 'components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from 'components/ui/tabs';
import { formatDate, parseBackendUtc } from 'lib/utils';
import { formatMinor } from 'features/subscription/utils/seat-pools';
import { formatNumber } from 'features/usage/utils/usage-format';
import { useDownloadInvoice, useInvoices, useLedger, useTopUps } from '../hooks/use-credits';
import type { LedgerEntryType, TopUpState } from '../types';
import { HistoryList } from './history-list';

const triggerClass = 'whitespace-normal px-2 py-2 text-center';
const dateOf = (value: string) => formatDate(parseBackendUtc(value));
const signed = (credits: number) => `${credits > 0 ? '+' : ''}${formatNumber(credits)}`;

const TopUpsPane = () => {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const { data, isLoading, isError, refetch } = useTopUps(page);
  const stateLabel: Record<TopUpState, string> = {
    Created: t('TOPUP_STATE_CREATED', 'Started'),
    CheckoutOpen: t('TOPUP_STATE_OPEN', 'Waiting for payment'),
    Paid: t('TOPUP_STATE_PAID', 'Paid'),
    Failed: t('TOPUP_STATE_FAILED', 'Failed'),
    Cancelled: t('TOPUP_STATE_CANCELLED', 'Cancelled'),
    Expired: t('TOPUP_STATE_EXPIRED', 'Expired'),
  };

  return (
    <HistoryList
      isLoading={isLoading}
      isError={isError}
      onRetry={refetch}
      emptyText={t('NO_TOPUPS', 'No top-ups yet. Your first one shows up here.')}
      page={page}
      total={data?.total}
      onPageChange={setPage}
      rows={(data?.items ?? []).map((topUp) => ({
        id: topUp.id,
        title: formatMinor(topUp.amountMinor, topUp.currencyCode),
        subtitle: dateOf(topUp.createdDate),
        value:
          topUp.creditsGranted == null
            ? undefined
            : t('CREDITS_COUNT', {
                credits: formatNumber(topUp.creditsGranted),
                defaultValue: '{{credits}} credits',
              }),
        badge: (
          <Badge variant={topUp.state === 'Paid' ? 'secondary' : 'outline'}>
            {stateLabel[topUp.state] ?? topUp.state}
          </Badge>
        ),
      }))}
    />
  );
};

const LedgerPane = () => {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const { data, isLoading, isError, refetch } = useLedger(page);
  const typeLabel: Record<LedgerEntryType, string> = {
    TopUp: t('LEDGER_TOPUP', 'Top-up'),
    Spend: t('LEDGER_SPEND', 'Credit use'),
    Refund: t('LEDGER_REFUND', 'Refund'),
    Adjustment: t('LEDGER_ADJUSTMENT', 'Adjustment'),
  };

  return (
    <HistoryList
      isLoading={isLoading}
      isError={isError}
      onRetry={refetch}
      emptyText={t('NO_CREDIT_USE', 'No credit activity yet.')}
      page={page}
      total={data?.total}
      onPageChange={setPage}
      rows={(data?.items ?? []).map((entry) => ({
        id: entry.id,
        title: typeLabel[entry.type] ?? entry.type,
        subtitle: [dateOf(entry.createdDate), entry.note].filter(Boolean).join(' - '),
        value: signed(entry.deltaCredits),
      }))}
    />
  );
};

const InvoicesPane = () => {
  const { t } = useTranslation();
  const { data, isLoading, isError, refetch } = useInvoices();
  const { mutate: download, isPending, variables } = useDownloadInvoice();

  return (
    <HistoryList
      isLoading={isLoading}
      isError={isError}
      onRetry={refetch}
      emptyText={t('NO_INVOICES', 'No invoices yet. Each paid top-up gets one.')}
      rows={(data?.items ?? []).map((invoice) => ({
        id: invoice.id,
        title: invoice.number,
        subtitle: dateOf(invoice.createdDate),
        value: formatMinor(invoice.grossMinor, invoice.currencyCode),
        badge: <Badge variant="outline">{invoice.deliveryState}</Badge>,
        action: (
          <Button
            variant="outline"
            size="sm"
            loading={isPending && variables?.id === invoice.id}
            disabled={isPending}
            onClick={() => download(invoice)}
            aria-label={t('DOWNLOAD_INVOICE_NAMED', {
              number: invoice.number,
              defaultValue: 'Download invoice {{number}}',
            })}
          >
            <Download aria-hidden="true" />
            {t('DOWNLOAD', 'Download')}
          </Button>
        ),
      }))}
    />
  );
};

export const CreditHistory = () => {
  const { t } = useTranslation();

  return (
    <div className="space-y-3">
      <h3 className="text-sm font-medium text-muted-foreground sm:text-base">
        {t('CREDIT_HISTORY', 'History')}
      </h3>
      <Tabs defaultValue="topups" className="space-y-3">
        <TabsList className="grid h-auto w-full grid-cols-3">
          <TabsTrigger value="topups" className={triggerClass}>
            {t('HISTORY_TOPUPS', 'Top-ups')}
          </TabsTrigger>
          <TabsTrigger value="use" className={triggerClass}>
            {t('HISTORY_CREDIT_USE', 'Credit use')}
          </TabsTrigger>
          <TabsTrigger value="invoices" className={triggerClass}>
            {t('HISTORY_INVOICES', 'Invoices')}
          </TabsTrigger>
        </TabsList>
        <TabsContent value="topups">
          <TopUpsPane />
        </TabsContent>
        <TabsContent value="use">
          <LedgerPane />
        </TabsContent>
        <TabsContent value="invoices">
          <InvoicesPane />
        </TabsContent>
      </Tabs>
    </div>
  );
};
